import PropTypes from 'prop-types';
import { applyMiddleware, createStore } from 'redux';
import { identity, mapValues } from 'lodash';

import actions from './redux/actions';
import createForm from './redux/form';
import { createScopedFormSelectors } from './redux/selectors';
import thunkMiddleware from 'redux-thunk';

/**
 * Represents a form reducer with selectors and actions bound to it to
 * promote ease of use between all the components in the library.
 */
export default class NucleusForm {
  constructor(formId, store, formSelector = identity) {
    this.id = formId;
    this.store = store;

    // Create contextual selectors to access the form.
    const scopedSelectors = createScopedFormSelectors(formSelector);
    this.selectors = mapValues(scopedSelectors,
      (selector) => (...args) => selector(store.getState(), ...args));

    // Create contextual actions to manipulate the specific form.
    this.actions = mapValues(actions, (action) => (...args) => store.dispatch(action(formId, ...args)));
    this.actions.submitForm = (submitCallback, options) => {
      return store.dispatch(actions.submitForm(formSelector, submitCallback, options));
    };
  }
}

/**
 * Creates a NucluesForm that is tied to a form reducer in a larger store.
 * Useful when the redcuer is connected to the applications overall redux store.
 */
export const createReduxNucleusForm = (formId, store, formSelector) => {
  return new NucleusForm(formId, store, formSelector);
};

/**
 * Creates a Nucleus form with a newly created redux store that only contains
 * a form reducer.
 */
export const createLocalNucleusForm = (formId = 'LocalForm') => {
  const formStore = createStore(createForm(formId), applyMiddleware(thunkMiddleware));
  return new NucleusForm(formId, formStore);
};

const nucleusFormSelectorsShape = PropTypes.shape({
  getForm: PropTypes.func.isRequired,
  getInputs: PropTypes.func.isRequired,
  getInput: PropTypes.func.isRequired
});

const nucleusFormActionsShape = PropTypes.shape({
  addInput: PropTypes.func.isRequired,
  changeInput: PropTypes.func.isRequired,
  focusInput: PropTypes.func.isRequired,
  blurInput: PropTypes.func.isRequired,
  removeInput: PropTypes.func.isRequired,
  setInputValidations: PropTypes.func.isRequired,
  setInputValidationValue: PropTypes.func.isRequired,
  validateInput: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired
});

export const nucleusFormShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  store: PropTypes.any.isRequired,
  actions: nucleusFormActionsShape.isRequired,
  selectors: nucleusFormSelectorsShape.isRequired
});
